import { Badge, Chip } from '@mui/material';

import { formatDateTime } from '../../../utils/formatting';
import { humanizeValue, str } from '../field-access';
import type { ResourceViewDefinition } from '../types';
import { statusTone } from './status-tone';

/**
 * Browse-only registrations (GDN-2729).
 *
 * These types have no bespoke admin page and don't need one — they only ever
 * get browsed. Registering `columns` upgrades them from the schema-derived
 * default (the first eight fields in schema order, no custom cells) to a table
 * an admin can actually read. No `header`/`sections`/`preview`: there is
 * nothing to show beyond the record, and the shell already renders that under
 * Fields and Raw JSON.
 *
 * Filters stay minimal on purpose — see `ResourceFilterKey`, the generic list
 * endpoint honors only the owner dimensions.
 */

const dateCell = (value: unknown) =>
  typeof value === 'string' ? formatDateTime(value) : '—';

const boolCell = (value: unknown) => (value === true ? 'Yes' : 'No');

const centsCell = (value: unknown) =>
  typeof value === 'number' ? `$${(value / 100).toFixed(2)}` : '—';

/** Red "deleted" marker beside a value, for types that soft-delete. */
const withDeleted = (
  render: (value: unknown) => string,
): ((value: unknown, row: Record<string, unknown>) => React.ReactNode) =>
  function DeletedAware(value, row) {
    return (
      <span className="flex items-center gap-1.5">
        {render(value)}
        {str(row, 'deleted_at') && (
          <span className="text-xs font-medium text-red-600">deleted</span>
        )}
      </span>
    );
  };

/**
 * Patient rows are deliberately identifier-only.
 *
 * The record carries name, DOB, email and address, but admin lists identify
 * patients by id — surfacing names here would reintroduce exactly what
 * GDN-2686 removed from the notes list. The full record is still one click
 * away under Fields for the cases that genuinely need it.
 */
export const patientView: ResourceViewDefinition = {
  label: 'Patient',
  columns: [
    {
      key: 'patient_id',
      label: 'Patient ID',
      render: withDeleted(value => (typeof value === 'string' ? value : '—')),
    },
    {
      key: 'patient_consent_on_file',
      label: 'Consent on file',
      render: boolCell,
    },
    {
      key: 'primary_insurance',
      label: 'Insurance',
      render: value => (value ? 'On file' : '—'),
    },
    { key: 'created_at', label: 'Created', render: dateCell },
  ],
};

/**
 * Claim rows avoid the subscriber/patient demographics the model carries
 * (`patient_first_name`, `patient_dob`, `primary_insured_*`) for the same
 * reason as patient.
 */
export const claimView: ResourceViewDefinition = {
  label: 'Claim',
  columns: [
    {
      key: 'status',
      label: 'Status',
      render: withDeleted(value =>
        typeof value === 'string' ? humanizeValue(value) : '—',
      ),
    },
    { key: 'claim_type', label: 'Type' },
    { key: 'service_date_from', label: 'Service date', render: dateCell },
    { key: 'total_charge_cents', label: 'Charged', render: centsCell },
    { key: 'balance_due_cents', label: 'Balance', render: centsCell },
    { key: 'insurance_payer_name', label: 'Payer' },
    { key: 'patient_id', label: 'Patient' },
  ],
  filters: [{ key: 'patient_id', label: 'Patient id', type: 'text' }],
};

export const documentReferenceView: ResourceViewDefinition = {
  label: 'Document reference',
  columns: [
    { key: 'title', label: 'Title' },
    {
      key: 'doc_kind',
      label: 'Kind',
      render: value =>
        typeof value === 'string' ? <Chip size="small" label={value} /> : '—',
    },
    { key: 'document_domain', label: 'Domain' },
    {
      key: 'processing_status',
      label: 'Processing',
      render: withDeleted(value =>
        typeof value === 'string' ? humanizeValue(value) : '—',
      ),
    },
    { key: 'effective_date', label: 'Effective', render: dateCell },
    { key: 'patient_id', label: 'Patient' },
  ],
  filters: [{ key: 'patient_id', label: 'Patient id', type: 'text' }],
};

export const encounterTypeView: ResourceViewDefinition = {
  label: 'Encounter type',
  columns: [
    { key: 'encounter_type', label: 'Name' },
    {
      key: 'owner_scope',
      label: 'Scope',
      render: value =>
        typeof value === 'string' ? <Chip size="small" label={value} /> : '—',
    },
    {
      key: 'default_duration',
      label: 'Duration',
      render: value => (typeof value === 'number' ? `${value} min` : '—'),
    },
    { key: 'default_modality', label: 'Modality' },
    { key: 'telehealth', label: 'Telehealth', render: boolCell },
    {
      // Encounter types archive rather than delete; an archived one still
      // appears in the list and must be distinguishable from a live one.
      key: 'archived_at',
      label: 'Archived',
      render: value =>
        typeof value === 'string' ? (
          <Badge color={statusTone('cancelled')}>
            {formatDateTime(value)}
          </Badge>
        ) : (
          '—'
        ),
    },
  ],
};
